import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqList } from '@/mocks/faq';
import ArticleCard from '@/components/feature/ArticleCard';
import { useEffect, useRef, useState } from 'react';

export default function HomePage() {
  const featuredArticles = articles.filter(a => a.featured).slice(0, 4);
  const latestArticles = [...articles].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 6);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.15 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((s) => observerRef.current?.observe(s));

    return () => observerRef.current?.disconnect();
  }, []);

  const isVisible = (id: string) => visibleSections.has(id);

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '高清无码指南',
    url: 'https://hd-uncensored-guide.readdy.site',
    description: '国内首个系统性聚焦高清视频编码与画质优化的专业技术内容平台。',
    email: 'contact@hd-guide.cn',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '海淀区',
      addressCountry: 'CN',
    },
  };

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '高清无码视频画质终极指南',
    url: 'https://hd-uncensored-guide.readdy.site',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://hd-uncensored-guide.readdy.site/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hd-uncensored-guide.readdy.site/' },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>

      <HeroSection />
      <CategorySection isVisible={isVisible} />
      <FeaturedSection articles={featuredArticles} isVisible={isVisible} />
      <LatestSection articles={latestArticles} isVisible={isVisible} />
      <FAQSection isVisible={isVisible} />
      <CTASection isVisible={isVisible} />
    </>
  );
}

function HeroSection() {
  return (
    <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Cinematic%20abstract%20digital%20art%20with%20flowing%20light%20trails%20and%20geometric%20patterns%20representing%20video%20encoding%20technology%2C%20warm%20amber%20and%20deep%20teal%20color%20palette%2C%20dark%20atmospheric%20background%20with%20luminescent%20data%20streams%2C%20futuristic%20elegant%20composition%2C%204K%20rendering%20quality&width=1600&height=900&seq=hero-bg-main&orientation=landscape"
        alt="高清无码视频画质终极指南"
        title="高清无码视频画质终极指南 - 首页"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>

      <div className="relative z-10 w-full text-center px-4 md:px-6">
        <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-background-50 mb-4 md:mb-6 leading-tight">
          高清无码视频画质
          <br />
          <span className="text-primary-400">终极指南</span>
        </h1>
        <p className="text-sm md:text-base text-background-200 max-w-2xl mx-auto mb-6 md:mb-8 leading-relaxed">
          深入解析视频编码标准、画质优化方案与无损压缩技术，帮助创作者与技术爱好者
          全面掌握高清无码内容的制作与分发最佳实践。
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/category/bianma-biaozhun"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-book-read-line"></i>
            开始探索
          </Link>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 px-6 py-3 bg-background-50/15 text-background-50 border border-background-50/30 rounded-md font-medium text-sm hover:bg-background-50/25 transition-colors whitespace-nowrap"
          >
            <i className="ri-information-line"></i>
            了解更多
          </Link>
        </div>
      </div>
    </section>
  );
}

function CategorySection({ isVisible }: { isVisible: (id: string) => boolean }) {
  return (
    <section id="categories" data-animate className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div
        className={`max-w-7xl mx-auto transition-all duration-700 ${
          isVisible('categories') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="text-center mb-10 md:mb-14">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">内容分类</h2>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            五大核心板块，系统化覆盖高清无码视频技术的方方面面
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 hover:border-primary-200 transition-all duration-300 flex flex-col items-center text-center"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3 md:mb-4 group-hover:bg-accent-200 transition-colors">
                <i className={`${cat.icon} text-xl md:text-2xl`}></i>
              </div>
              <h3 className="font-heading text-base text-foreground-900 mb-2">{cat.name}</h3>
              <p className="text-xs text-foreground-500 leading-relaxed mb-3">{cat.description}</p>
              <span className="text-xs text-primary-600 font-medium whitespace-nowrap">
                {cat.count} 篇文章
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedSection({ articles: featuredArticles, isVisible }: { articles: typeof import('@/mocks/articles').articles; isVisible: (id: string) => boolean }) {
  return (
    <section id="featured" data-animate className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div
        className={`max-w-7xl mx-auto transition-all duration-700 ${
          isVisible('featured') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="text-center mb-10 md:mb-14">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">精选文章</h2>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            编辑团队精心挑选的高质量技术文章，涵盖最受关注的高清无码视频话题
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {featuredArticles.map((article) => (
            <ArticleCard key={article.slug} article={article} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestSection({ articles: latestArticles, isVisible }: { articles: typeof import('@/mocks/articles').articles; isVisible: (id: string) => boolean }) {
  return (
    <section id="latest" data-animate className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div
        className={`max-w-7xl mx-auto transition-all duration-700 ${
          isVisible('latest') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="text-center mb-10 md:mb-14">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">最新文章</h2>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            持续更新的高清无码编码技术内容，紧跟行业最新发展动态
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {latestArticles.map((article) => (
            <ArticleCard key={article.slug} article={article} />
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/category/bianma-biaozhun"
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-primary-300 text-primary-700 rounded-md text-sm font-medium hover:bg-primary-50 transition-colors whitespace-nowrap"
          >
            查看全部文章
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

function FAQSection({ isVisible }: { isVisible: (id: string) => boolean }) {
  const sliced = faqList.slice(0, 6);

  return (
    <section id="faq" data-animate className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div
        className={`max-w-3xl mx-auto transition-all duration-700 ${
          isVisible('faq') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="text-center mb-10 md:mb-14">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">常见问题</h2>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            关于高清无码视频编码与画质优化的常见疑问解答
          </p>
        </div>

        <div className="space-y-3">
          {sliced.map((faq, idx) => (
            <details key={idx} className="group border border-background-200/70 rounded-lg bg-background-50">
              <summary className="flex items-center justify-between cursor-pointer p-4 md:p-5 font-heading text-sm md:text-base text-foreground-800 list-none">
                <span className="pr-4">{faq.question}</span>
                <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-foreground-400 group-open:hidden">
                  <i className="ri-add-line"></i>
                </span>
                <span className="flex-shrink-0 w-5 h-5 hidden items-center justify-center text-foreground-400 group-open:flex">
                  <i className="ri-subtract-line"></i>
                </span>
              </summary>
              <div className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed">
                {faq.answer}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection({ isVisible }: { isVisible: (id: string) => boolean }) {
  return (
    <section id="cta" data-animate className="relative py-16 md:py-24 px-4 md:px-6 overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20background%20with%20soft%20flowing%20light%20patterns%2C%20subtle%20geometric%20overlay%2C%20dark%20amber%20and%20deep%20teal%20tones%2C%20elegant%20minimal%20atmosphere%2C%20cinematic%20lighting%20quality&width=1600&height=600&seq=cta-bg-main&orientation=landscape"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>

      <div
        className={`relative z-10 max-w-2xl mx-auto text-center transition-all duration-700 ${
          isVisible('cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-4">
          准备好提升你的视频画质了吗？
        </h2>
        <p className="text-sm md:text-base text-background-300 mb-6 md:mb-8 leading-relaxed">
          加入 15 万+ 创作者与技术爱好者，获取最新高清无码视频编码技术资讯与实战指南。
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/category/zhizuo-liucheng"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-rocket-line"></i>
            立即开始学习
          </Link>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 px-6 py-3 bg-background-50/15 text-background-50 border border-background-50/30 rounded-md font-medium text-sm hover:bg-background-50/25 transition-colors whitespace-nowrap"
          >
            <i className="ri-team-line"></i>
            了解我们的团队
          </Link>
        </div>
      </div>
    </section>
  );
}